import React, { useEffect, useState } from "react";
import {
  isStaleStationIdentity,
  recommendedStationIdentity,
} from "../../../../utils/stationIdentity";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import {
  ProspectStation,
  SaveStationCredentialsPayload,
} from "../../../../types/onboarding";

interface StationCredentialsDialogProps {
  // The station being corrected; null keeps the dialog closed.
  station: ProspectStation | null;
  onHide: () => void;
  onSubmit: (payload: SaveStationCredentialsPayload) => Promise<boolean>;
}

/**
 * Corrects one station's access data (CWA-91).
 *
 * A recovery path, not the normal flow: the station identity and access key are
 * generated automatically when station IDs are assigned. This dialog only SETS a
 * key — leaving the field empty asks the backend for a brand-new one. Reading the
 * current key is a separate, audited action on the station row.
 */
const StationCredentialsDialog: React.FC<StationCredentialsDialogProps> = ({
  station,
  onHide,
  onSubmit,
}) => {
  const [chargePointId, setChargePointId] = useState("");
  const isStale = !!station && isStaleStationIdentity(station);
  const recommended = station ? recommendedStationIdentity(station) : "";
  const [accessKey, setAccessKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setChargePointId(station?.charge_point_id ?? "");
    setAccessKey("");
    setError(null);
    setSubmitting(false);
  }, [station]);

  const submit = async () => {
    const trimmed = chargePointId.trim();
    if (!trimmed) {
      setError("Please enter the station ID.");
      return;
    }
    setSubmitting(true);
    try {
      const key = accessKey.trim();
      const ok = await onSubmit({
        charge_point_id: trimmed,
        // Omitted on purpose when blank — that is what makes the backend
        // generate a new key rather than keeping the old one.
        ...(key ? { authorization_key: key } : {}),
      });
      if (ok) onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={submit}
        loading={submitting}
        disabled={submitting}
      />
    </div>
  );

  return (
    <Dialog
      header={`Correct access data${station ? ` — ${station.station_id}` : ""}`}
      visible={!!station}
      onHide={onHide}
      footer={footer}
      style={{ width: "34rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="text-500 mt-0">
        Access data is created automatically when station IDs are assigned. Use
        this only to correct it — saving issues a new access key, so the customer
        needs the configuration email again.
      </p>

      <div className="field p-fluid mb-3">
        <label htmlFor="station-identity" className="block mb-1">
          Station ID
        </label>
        <InputText
          id="station-identity"
          value={chargePointId}
          onChange={(e) => {
            setChargePointId(e.target.value);
            setError(null);
          }}
          placeholder={recommended}
          aria-label="Station ID"
        />
        <small className="text-500">
          This is the station's name in eRound and the identity the wallbox
          presents when it connects — the same value in both places.
        </small>
        {/* Offered rather than applied: a station really can be registered in
            eRound under a different technical name, so an agent decides. */}
        {isStale && (
          <div className="mt-2">
            <Message
              severity="warn"
              className="w-full"
              text={`This station is stored as "${station?.charge_point_id}", which eRound will not match.`}
            />
            <Button
              label={`Use ${recommended}`}
              icon="pi pi-wand"
              text
              size="small"
              className="p-0 mt-1"
              onClick={() => {
                setChargePointId(recommended);
                setError(null);
              }}
            />
          </div>
        )}
      </div>

      <div className="field p-fluid">
        <label htmlFor="access-key" className="block mb-1">
          Access key
        </label>
        <InputText
          id="access-key"
          value={accessKey}
          onChange={(e) => setAccessKey(e.target.value)}
          placeholder="Leave empty to generate a new one"
          aria-label="Access key"
        />
        <small className="text-500">
          Leave this empty to have a new one generated, or type a specific key to
          set it. To read the current key, expand the station row instead —
          saving a new one here replaces it.
        </small>
      </div>
    </Dialog>
  );
};

export default StationCredentialsDialog;
